import React, { useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Music, Car, PartyPopper, Home, MessageCircle, Plane, Users } from 'lucide-react';
import { appStoreLinks } from '../data/mock';
import Layout from '../components/layout/Layout';

// Modern Gen-Z headphone icon component
const ModernHeadphoneIcon = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M3 18V12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12V18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    <rect x="1" y="14" width="5" height="8" rx="2" fill="currentColor" />
    <rect x="18" y="14" width="5" height="8" rx="2" fill="currentColor" />
    <circle cx="3.5" cy="18" r="1.5" fill="white" opacity="0.3" />
    <circle cx="20.5" cy="18" r="1.5" fill="white" opacity="0.3" />
  </svg>
);

// Equalizer visualization
const Equalizer = ({ className = "" }) => (
  <div className={`flex items-end gap-[3px] ${className}`} aria-hidden="true">
    {[...Array(7)].map((_, i) => (
      <span key={i} className="equalizer-bar" />
    ))}
  </div>
);

// Concert crowd image for hero section
const CROWD_IMAGE = 'https://images.unsplash.com/photo-1565806938645-6073fa4c8c41?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1920';

// Download buttons component
const DownloadButtons = ({ size = 'md' }) => {
  const isSm = size === 'sm';
  return (
    <div className="flex flex-row gap-4 justify-center">
      <a 
        href={appStoreLinks.apple} 
        target="_blank" 
        rel="noopener noreferrer"
        className={`download-btn-glow inline-flex items-center gap-2.5 text-white ${isSm ? 'px-4 py-2.5' : 'px-5 py-3'} rounded-xl font-medium text-sm cursor-pointer`}
      >
        <svg className={`${isSm ? 'w-4 h-4' : 'w-5 h-5'}`} viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
        <div className="flex flex-col items-start leading-tight">
          <span className={`${isSm ? 'text-[8px]' : 'text-[10px]'} download-label-gradient font-medium`}>Download on the</span>
          <span className={`${isSm ? 'text-xs' : 'text-sm'} font-semibold -mt-0.5`}>App Store</span>
        </div>
      </a>
      <a 
        href={appStoreLinks.google}
        target="_blank"
        rel="noopener noreferrer"
        className={`download-btn-glow inline-flex items-center gap-2.5 text-white ${isSm ? 'px-4 py-2.5' : 'px-5 py-3'} rounded-xl font-medium text-sm cursor-pointer`}
      >
        <svg className={`${isSm ? 'w-4 h-4' : 'w-5 h-5'}`} viewBox="0 0 24 24" fill="currentColor">
          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
        </svg>
        <div className="flex flex-col items-start leading-tight">
          <span className={`${isSm ? 'text-[8px]' : 'text-[10px]'} download-label-gradient font-medium`}>Get it on</span>
          <span className={`${isSm ? 'text-xs' : 'text-sm'} font-semibold -mt-0.5`}>Google Play</span>
        </div>
      </a>
    </div>
  );
};

const HomePage = () => {
  const revealRefs = useRef([]);

  const addRevealRef = useCallback((el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Scroll reveal observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    revealRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[80vh] md:min-h-[90vh] flex items-center justify-center overflow-hidden -mt-16 md:-mt-20 grain-overlay">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-indigo-50 md:from-indigo-100 via-purple-50/60 md:via-purple-50 to-white" />
          {/* Animated gradient mesh — lava lamp orbs */}
          <div className="absolute top-[5%] right-[5%] w-[550px] h-[550px] bg-purple-300/30 md:bg-purple-300/55 blob-morph blur-[90px]" style={{ animation: 'blobMorph 12s ease-in-out infinite, blobDrift1 14s ease-in-out infinite' }} />
          <div className="absolute top-[30%] left-[5%] w-[500px] h-[500px] bg-blue-300/25 md:bg-blue-300/50 blob-morph blur-[80px]" style={{ animation: 'blobMorph 15s ease-in-out infinite reverse, blobDrift2 16s ease-in-out infinite' }} />
          <div className="absolute top-[50%] right-[20%] w-[400px] h-[400px] bg-indigo-300/20 md:bg-indigo-300/45 blob-morph blur-[70px]" style={{ animation: 'blobMorph 13s ease-in-out infinite, blobDrift3 15s ease-in-out infinite' }} />
          <div className="absolute top-[15%] left-[40%] w-[350px] h-[350px] bg-pink-300/5 md:bg-pink-300/10 blob-morph blur-[80px]" style={{ animation: 'blobMorph 16s ease-in-out infinite reverse, blobDrift1 18s ease-in-out infinite reverse' }} />
          <div className="absolute top-[60%] left-[30%] w-[300px] h-[300px] bg-violet-300/15 md:bg-violet-300/35 blob-morph blur-[65px]" style={{ animation: 'blobMorph 11s ease-in-out infinite, blobDrift2 13s ease-in-out infinite reverse' }} />
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-56 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none" />
        
        <div className="relative z-20 w-full px-6 pt-24 md:pt-32 pb-4 md:pb-20">
          {/* Mobile Layout */}
          <div className="lg:hidden flex flex-col items-center text-center">
            <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 leading-[1.05] tracking-tight mb-6">
              <span className="block">Music.</span>
              <span className="block gradient-text-hero">Movement.</span>
              <span className="block">Moments.</span>
            </h1>
            
            <p className="text-xs sm:text-sm text-gray-600 mb-8 max-w-[280px] sm:max-w-sm leading-relaxed">
              Listen together in real time.<br />
              Turn playlists into memory capsules.<br />
              Share music the way it was meant to be shared.
            </p>
            
            <DownloadButtons size="md" />
          </div>

          {/* Desktop Layout - Centered */}
          <div className="hidden lg:flex flex-col items-center text-center max-w-3xl mx-auto">
            <h1 className="text-7xl font-bold text-gray-900 leading-[1.05] tracking-tight mb-6">
              <span className="block">Music.</span>
              <span className="block gradient-text-hero">Movement.</span>
              <span className="block">Moments.</span>
            </h1>
            
            <p className="text-lg text-gray-600 mb-8 max-w-md leading-relaxed">
              Listen together in real time.<br />
              Turn playlists into memory capsules.<br />
              Share music the way it was meant to be shared.
            </p>
            
            <DownloadButtons size="md" />
          </div>
        </div>
      </section>

      {/* Two Ways Section */}
      <section className="pt-4 pb-16 md:py-28 bg-white relative grain-overlay">
        {/* Bottom fade for seamless blend into next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-transparent to-white pointer-events-none z-10" />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-10 md:mb-16">
            <div className="relative inline-block">
              {/* Equalizer spanning full width of title */}
              <div className="absolute -top-7 md:-top-8 left-0 right-0 flex items-end opacity-35 pointer-events-none" aria-hidden="true" style={{ gap: '2px' }}>
                {[...Array(48)].map((_, i) => {
                  const heights = [14,22,32,18,28,12,26,20,30,16,24,34,15,27,19,33,13,25,21,31,17,29,23,35,14,28,18,32,16,24,20,30,22,15,28,19,34,12,26,21,30,17,25,33,14,27,20,31];
                  return <span key={i} className="equalizer-bar" style={{ flex: '1 1 0', minWidth: 2, height: heights[i], animationDelay: `${i * 0.05}s` }} />;
                })}
              </div>
              <h2 
                ref={addRevealRef}
                className="scroll-reveal text-4xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 whitespace-nowrap"
              >
                Ways to <span className="gradient-text">Connect</span>
              </h2>
            </div>
            <p ref={addRevealRef} className="scroll-reveal scroll-reveal-delay-1 text-gray-600 text-sm md:text-lg max-w-xl mx-auto mt-4">
              Share the sound. Feel the moment together.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {/* Sessions Card */}
            <Link 
              to="/sessions"
              ref={addRevealRef}
              className="scroll-reveal scroll-reveal-delay-1 card-tilt group rounded-3xl p-6 md:p-8 bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/50 hover:border-blue-300 transition-all duration-300"
            >
              <div className="relative w-12 h-12 mb-5">
                <div className="hidden md:block absolute inset-0 rounded-2xl bg-blue-400/30 animate-ping" style={{ animationDuration: '3s' }} />
                <div className="hidden md:block absolute inset-0 rounded-2xl bg-blue-400/20 animate-pulse" style={{ animationDuration: '3s' }} />
                <div className="relative w-full h-full rounded-2xl bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/30 md:animate-session-pulse">
                  <ModernHeadphoneIcon size={24} className="text-white" />
                </div>
              </div>
              
              <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-2">
                <span className="gradient-text-blue">Sessions</span>
              </h3>
              
              <p className="text-gray-600 mb-4 text-xs md:text-sm leading-relaxed">
                Listen together in real time. Same song, same moment, anywhere in the world.
              </p>
              
              <span className="inline-flex items-center gap-2 text-blue-600 font-medium group-hover:gap-3 transition-all text-xs md:text-sm">
                Start a session <ArrowRight size={16} />
              </span>
            </Link>

            {/* Playlists Card */}
            <Link 
              to="/playlists"
              ref={addRevealRef}
              className="scroll-reveal scroll-reveal-delay-2 card-tilt group rounded-3xl p-6 md:p-8 bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200/50 hover:border-purple-300 transition-all duration-300"
            >
              <div className="w-12 h-12 mb-5 rounded-2xl bg-purple-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
                <Music className="text-white" size={24} />
              </div>
              
              <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-2">
                <span className="gradient-text">Playlists</span>
              </h3>
              
              <p className="text-gray-600 mb-4 text-xs md:text-sm leading-relaxed">
                Songs, photos, and videos. Capture how the music felt in one place.
              </p>
              
              <span className="inline-flex items-center gap-2 text-purple-600 font-medium group-hover:gap-3 transition-all text-xs md:text-sm">
                Create a playlist <ArrowRight size={16} />
              </span>
            </Link>

            {/* Music DM's Card */}
            <div 
              ref={addRevealRef}
              className="scroll-reveal scroll-reveal-delay-3 card-tilt group rounded-3xl p-6 md:p-8 bg-gradient-to-br from-green-50 to-emerald-100/50 border border-green-200/50 hover:border-green-300 transition-all duration-300"
            >
              <div className="w-12 h-12 mb-5 rounded-2xl bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/20">
                <MessageCircle className="text-white" size={24} />
              </div>
              
              <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-2">
                <span className="text-green-600">Music DM's</span>
              </h3>
              
              <p className="text-gray-600 text-xs md:text-sm leading-relaxed">
                Share music and files all in one place.
              </p>
            </div>

            {/* Following Card */}
            <div 
              ref={addRevealRef}
              className="scroll-reveal scroll-reveal-delay-4 card-tilt group rounded-3xl p-6 md:p-8 bg-gradient-to-br from-amber-50 to-orange-100/50 border border-amber-200/50 hover:border-amber-300 transition-all duration-300"
            >
              <div className="w-12 h-12 mb-5 rounded-2xl bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Users className="text-white" size={24} />
              </div>
              
              <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-2">
                <span className="text-amber-600">Following</span>
              </h3>
              
              <p className="text-gray-600 text-xs md:text-sm leading-relaxed">
                Follow friends, family, artists, and music lovers around the world. Discover new music.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Real Use Cases Section */}
      <section className="py-12 md:py-20 relative overflow-hidden grain-overlay">
        {/* Seamless top blend - larger on mobile */}
        <div className="absolute -top-24 left-0 right-0 h-48 md:h-32 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none" />
        {/* Background with smooth ombre + morphing blobs */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-indigo-100/35 md:via-indigo-100/70 to-white" />
        <div className="absolute top-10 right-10 w-[450px] h-[450px] bg-purple-300/20 md:bg-purple-300/40 blob-morph blur-[100px]" />
        <div className="absolute top-1/3 left-10 w-[400px] h-[400px] bg-blue-300/15 md:bg-blue-300/30 blob-morph blur-[80px]" style={{ animationDelay: '-4s' }} />
        <div className="absolute top-1/2 right-1/4 w-[300px] h-[300px] bg-indigo-300/12 md:bg-indigo-300/25 blob-morph blur-[70px]" style={{ animationDelay: '-8s' }} />
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-8 md:mb-12">
            <h2 
              ref={addRevealRef}
              className="scroll-reveal text-3xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4"
            >
              Music For <span className="gradient-text">Every Moment</span>
            </h2>
            <p ref={addRevealRef} className="scroll-reveal scroll-reveal-delay-1 text-gray-600 text-sm md:text-lg max-w-xl mx-auto">
              Moments where Tap In changes the game.
            </p>
          </div>

          {/* 3 columns - glassmorphism cards with stagger */}
          <div className="grid grid-cols-3 gap-3 md:gap-5">
            {/* Events */}
            <div 
              ref={addRevealRef}
              className="scroll-reveal scroll-reveal-delay-1 rounded-xl border-2 border-purple-500 p-3 md:p-5 flex flex-col items-center text-center md:transition-all md:duration-300 hover:scale-[1.05] hover:shadow-lg hover:shadow-purple-500/10 glass-card"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center mb-2 md:mb-3 bg-purple-100">
                <PartyPopper className="text-purple-600" size={20} />
              </div>
              <h3 className="text-xs md:text-base font-bold text-gray-900">Events</h3>
              <p className="text-gray-500 text-[10px] md:text-xs leading-relaxed hidden md:block mt-1">
                Guests add requests pre-event, and photos during.
              </p>
            </div>

            {/* Road Trips */}
            <div 
              ref={addRevealRef}
              className="scroll-reveal scroll-reveal-delay-2 rounded-xl border-2 border-blue-500 p-3 md:p-5 flex flex-col items-center text-center md:transition-all md:duration-300 hover:scale-[1.05] hover:shadow-lg hover:shadow-blue-500/10 glass-card"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center mb-2 md:mb-3 bg-blue-100">
                <Car className="text-blue-600" size={20} />
              </div>
              <h3 className="text-xs md:text-base font-bold text-gray-900">Road Trips</h3>
              <p className="text-gray-500 text-[10px] md:text-xs leading-relaxed hidden md:block mt-1">
                Music everyone can share with no interruptions.
              </p>
            </div>

            {/* Travel */}
            <div 
              ref={addRevealRef}
              className="scroll-reveal scroll-reveal-delay-3 rounded-xl border-2 border-orange-500 p-3 md:p-5 flex flex-col items-center text-center md:transition-all md:duration-300 hover:scale-[1.05] hover:shadow-lg hover:shadow-orange-500/10 glass-card"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center mb-2 md:mb-3 bg-orange-100">
                <Plane className="text-orange-600" size={20} />
              </div>
              <h3 className="text-xs md:text-base font-bold text-gray-900">Travel</h3>
              <p className="text-gray-500 text-[10px] md:text-xs leading-relaxed hidden md:block mt-1">
                Songs and photos all in one place.
              </p>
            </div>
          </div>
        </div>
        {/* Seamless bottom blend - larger on mobile */}
        <div className="absolute bottom-0 left-0 right-0 h-48 md:h-24 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none" />
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-28 bg-white relative grain-overlay">
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 
            ref={addRevealRef}
            className="scroll-reveal text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6"
          >
            Ready to <span className="gradient-text">Tap In</span>?
          </h2>
          <p 
            ref={addRevealRef}
            className="scroll-reveal scroll-reveal-delay-1 text-xs sm:text-sm md:text-xl text-gray-600 mb-8 md:mb-10 whitespace-nowrap"
          >
            Join thousands sharing music the way it was meant to be.
          </p>
          
          <div ref={addRevealRef} className="scroll-reveal scroll-reveal-delay-2 flex flex-row gap-4 justify-center">
            <a 
              href={appStoreLinks.apple} 
              target="_blank" 
              rel="noopener noreferrer"
              className="download-btn-glow inline-flex items-center gap-2 text-white px-4 md:px-6 py-3 md:py-3.5 rounded-xl font-medium text-sm cursor-pointer"
            >
              <svg className="w-4 md:w-5 h-4 md:h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="flex flex-col items-start leading-tight">
                <span className="text-[8px] md:text-[10px] download-label-gradient font-medium">Download on the</span>
                <span className="text-xs md:text-sm font-semibold -mt-0.5">App Store</span>
              </div>
            </a>
            <a 
              href={appStoreLinks.google}
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn-glow inline-flex items-center gap-2 text-white px-4 md:px-6 py-3 md:py-3.5 rounded-xl font-medium text-sm cursor-pointer"
            >
              <svg className="w-4 md:w-5 h-4 md:h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div className="flex flex-col items-start leading-tight">
                <span className="text-[8px] md:text-[10px] download-label-gradient font-medium">Get it on</span>
                <span className="text-xs md:text-sm font-semibold -mt-0.5">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
